"use client";

import { METRICS_EVENTS, FEATURE_FLAGS } from "@cede/types";
import { formatShortAddress } from "@cede/utils";
import { CurrencySelector } from "@/components/features/currency";
import { NotificationDropdown } from "@/components/features/notifications";
import { AddressText } from "@/components/shared/AddressText/AddressText";
import { UserAvatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { SkeletonElement } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useHubUser, useClipboard } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { cn } from "@/lib/utils";
import { Copy, LogOut, Check, Sparkles } from "lucide-react";

export const ProfileHeader = () => {
  const { hubUser, userFriendlyDefiAddress, logout, nftImages } = useHubUser();
  const { trackEvent } = useMetricsEvents();
  const { isCopied, handleCopy } = useClipboard();
  const isHighlightEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AMBASSADOR_HUB_HIGHLIGHT);

  const showAmbassador = Boolean(hubUser?.isAmbassador && isHighlightEnabled);

  const handleLogout = () => {
    trackEvent(METRICS_EVENTS.LOG_OUT, {
      lastLogoutDate: new Date().toISOString(),
      source: "profile_page",
    });
    logout();
  };

  const handleCopyAddress = async () => {
    const textToCopy = hubUser?.defiAddress || userFriendlyDefiAddress;
    if (textToCopy) {
      await handleCopy(textToCopy);
    }
  };

  return (
    <div
      className={cn(
        "w-full flex flex-row items-center justify-between",
        // Mobile: no padding, no background
        "md:px-6 md:py-5",
        "md:bg-cede-bg-component md:border border-cede-border-container md:rounded-xl md:backdrop-blur-xl",
      )}
    >
      {/* Left side: Wallet Address */}
      <div className="flex flex-row items-center gap-3 md:gap-4">
        {/* Avatar */}
        <UserAvatar
          identifier={hubUser?.defiAddress ?? hubUser?.id}
          src="/avatar.png"
          alt="Profile"
          size={40}
          className="w-9 h-9 md:w-10 md:h-10 flex-shrink-0"
        />

        {/* Address and Copy Button */}
        <div className="flex flex-col items-start gap-1.5">
          {!hubUser?.defiAddress ? (
            <SkeletonElement className="h-7 w-36" />
          ) : (
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2">
              <div className="flex items-center gap-2">
                <AddressText asChild>
                  <span className="h5 md:h4 font-semibold text-cede-text-primary">
                    {formatShortAddress(userFriendlyDefiAddress ?? "")}
                  </span>
                </AddressText>
                {/* Copy Button */}
                <Tooltip open={isCopied}>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleCopyAddress}
                      variant="secondary"
                      size="icon"
                      className="w-8 h-8 hover:bg-cede-bg-accent/50 text-cede-primary"
                      aria-label="Copy address"
                    >
                      {isCopied ? (
                        <Check className="w-4 h-4 animate-in fade-in-0 zoom-in-50 duration-200" />
                      ) : (
                        <Copy className="w-4 h-4 animate-in fade-in-0 zoom-in-50 duration-200" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copied!</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {showAmbassador && (
                <span
                  className="inline-flex self-start md:self-auto items-center gap-1.5 rounded-full border border-white/10 px-2 py-[6px] text-[10px] font-semibold uppercase tracking-[0.05em] md:ml-1 text-[#0f0f11]"
                  style={{
                    background: `linear-gradient(120deg, var(--color-ambassador-accent) 0%, color-mix(in srgb, var(--color-ambassador-accent) 80%, transparent) 60%, color-mix(in srgb, var(--color-ambassador-accent) 67%, transparent) 100%)`,
                    boxShadow: `0 10px 32px -18px color-mix(in srgb, var(--color-ambassador-accent) 67%, transparent)`,
                  }}
                >
                  <Sparkles className="h-3 w-3" />
                  Ambassador
                </span>
              )}
            </div>
          )}
        </div>

        {/* NFT Icons */}
        {nftImages.map((image, index) => (
          <div
            key={`nft-${index}`}
            className={cn(
              "relative ml-2 inline-block overflow-hidden rounded-full p-[3px] leading-[0]",
              "before:content-[''] before:absolute before:inset-0 before:rounded-full",
              "before:bg-[conic-gradient(from_0deg,#8a5a00,#d4af37,#fff6bf,#d4af37,#8a5a00)]",
              "before:animate-[spin_3s_linear_infinite]",
              "shadow-[0_0_12px_rgba(250,204,21,.35)]",
              "motion-reduce:before:animate-none",
            )}
          >
            <UserAvatar
              identifier={`NFT-${index}`}
              src={image}
              alt="NFT"
              size={40}
              className="relative z-10 block h-9 w-9 rounded-full md:h-10 md:w-10"
            />
          </div>
        ))}
      </div>

      {/* Right side: Log Out Button - Desktop only */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 md:hidden">
          <CurrencySelector source="profile" />
          <NotificationDropdown />
        </div>
        {!hubUser?.defiAddress ? (
          <SkeletonElement className="hidden md:block h-9 w-24" />
        ) : (
          <Button
            onClick={handleLogout}
            disabled={!hubUser?.defiAddress}
            variant="destructive"
            size="medium"
            rightIcon={<LogOut className="w-4 h-4" />}
            className="hidden md:flex"
          >
            Log-out
          </Button>
        )}
      </div>
    </div>
  );
};
